import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetAdminStats,
    GetAdminParticipants,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    SetParticipantWinner,
    ToggleAdminDynamicStatus,
    ResetAdminDynamic,
    GetExportExcelUrl,
    formatMexicoDate,
    SendQROverWebSocket,
    GetPublicConfig
} from '../../../../utilities/BL/MemoryIndividual';

import { MemoryIndividualDisqualifyModal } from './MemoryIndividualDisqualifyModal';
import { MemoryIndividualThemeModal } from './MemoryIndividualThemeModal';

/**
 * Panel de Control Administrativo (Tile ID 51) para la dinámica 'Memorama Individual'.
 * Provee embudo de conversión en vivo, padrón oficial paginado y filtrado,
 * proyección de QR a pantalla gigante, gestión de temas en 1 clic y zona de reinicio de jornada.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.getOutOfTheDynamic - Callback para regresar al Dashboard principal.
 */
export function MemoryIndividualBoard({ getOutOfTheDynamic }) {
    const { showMessage } = useFloatMessage();

    // ==========================================
    // ESTADOS PRINCIPALES DE DATOS
    // ==========================================
    const [stats, setStats] = useState({
        qrScan: 0,
        pageView: 0,
        formStart: 0,
        formComplete: 0,
        gameStart: 0,
        gameComplete: 0,
        totalParticipants: 0,
        completedParticipants: 0,
        winnerParticipants: 0,
        disqualifiedParticipants: 0,
        pendingParticipants: 0
    });

    const [participants, setParticipants] = useState([]);
    const [totalParticipantsCount, setTotalParticipantsCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [isDynamicActive, setIsDynamicActive] = useState(true);

    const [isLoading, setIsLoading] = useState(false);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);

    // ==========================================
    // BÚSQUEDA Y PAGINACIÓN
    // ==========================================
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'completed' | 'winner' | 'disqualified'
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // ==========================================
    // MODALES Y DIÁLOGOS
    // ==========================================
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [disqualifyTarget, setDisqualifyTarget] = useState(null);
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    // WebSocket ref para escuchar eventos en vivo
    const webSocketRef = useRef(null);

    // Debounce de 300ms para búsqueda
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    /**
     * Carga las estadísticas administrativas del embudo.
     */
    const loadStats = useCallback(async () => {
        try {
            const res = await GetAdminStats();
            if (res && res.success && res.data) {
                const d = res.data;
                setStats({
                    qrScan: d.qrScan ?? 0,
                    pageView: d.pageView ?? 0,
                    formStart: d.formStart ?? 0,
                    formComplete: d.formComplete ?? 0,
                    gameStart: d.gameStart ?? 0,
                    gameComplete: d.gameComplete ?? 0,
                    totalParticipants: d.totalParticipants ?? 0,
                    completedParticipants: d.completedParticipants ?? 0,
                    winnerParticipants: d.winnerParticipants ?? 0,
                    disqualifiedParticipants: d.disqualifiedParticipants ?? 0,
                    pendingParticipants: d.pendingParticipants ?? 0
                });
            }
        } catch (err) {
            console.error("Error al cargar estadísticas (BL):", err);
        }
    }, []);

    /**
     * Carga el padrón oficial de participantes con los filtros actuales.
     */
    const loadParticipants = useCallback(async () => {
        try {
            const res = await GetAdminParticipants({
                search: debouncedSearch,
                status: statusFilter,
                page: currentPage,
                pageSize: pageSize
            });

            if (res && res.success && res.data) {
                const paged = res.data;
                setParticipants(paged.items || []);
                setTotalParticipantsCount(paged.totalCount ?? (paged.items?.length || 0));
                setTotalPages(paged.totalPages || 1);
            } else {
                setParticipants([]);
                setTotalParticipantsCount(0);
                setTotalPages(1);
            }
        } catch (err) {
            console.error("Error al cargar participantes (BL):", err);
            setParticipants([]);
        }
    }, [debouncedSearch, statusFilter, currentPage, pageSize]);

    /**
     * Consulta el estado de activación de la dinámica.
     */
    const loadConfig = useCallback(async () => {
        try {
            const res = await GetPublicConfig();
            if (res && res.success && res.data) {
                if (res.data.isDynamicActive !== undefined) {
                    setIsDynamicActive(Boolean(res.data.isDynamicActive));
                }
            }
        } catch (err) {
            console.warn("Fallo no bloqueante al consultar configuración pública:", err);
        }
    }, []);

    /**
     * Sincronización general de datos.
     */
    const refreshAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            await Promise.all([loadStats(), loadParticipants(), loadConfig()]);
            const now = new Date();
            setLastSyncTime(
                `${now.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${now.toLocaleTimeString('es-MX', { hour12: false })} hrs`
            );
        } catch (error) {
            console.error("Error en sincronización general:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al sincronizar datos del panel administrativo.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [loadStats, loadParticipants, loadConfig, showMessage]);

    // Montaje inicial y recarga al cambiar filtros de tabla
    useEffect(() => {
        refreshAllData();
    }, [refreshAllData]);

    // WebSocket para sincronización en tiempo real
    useEffect(() => {
        let ws = null;
        let isMounted = true;

        const connectWS = () => {
            try {
                let wsUrl = process.env.REACT_APP_API_URL_WSS;
                if (!wsUrl && process.env.REACT_APP_API_URL) {
                    wsUrl = process.env.REACT_APP_API_URL.replace(/^http/, 'ws');
                    if (wsUrl.endsWith('/api/')) wsUrl = wsUrl.replace(/\/api\/$/, '/ws');
                    else if (wsUrl.endsWith('/api')) wsUrl = wsUrl.replace(/\/api$/, '/ws');
                    else if (!wsUrl.endsWith('/ws')) wsUrl = wsUrl.endsWith('/') ? `${wsUrl}ws` : `${wsUrl}/ws`;
                }

                if (!wsUrl && typeof window !== 'undefined') {
                    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                    wsUrl = `${protocol}//${window.location.host}/ws`;
                }

                if (wsUrl) {
                    const finalUrl = `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}tipo=superadmin`;
                    ws = new WebSocket(finalUrl);
                    webSocketRef.current = ws;

                    ws.onmessage = (event) => {
                        if (!isMounted) return;
                        try {
                            const data = JSON.parse(event.data);
                            if (
                                data.type === 'MEMORY_INDIVIDUAL_METRIC' ||
                                data.type === 'MEMORY_INDIVIDUAL_PARTICIPANT_REGISTERED' ||
                                data.type === 'MEMORY_INDIVIDUAL_GAME_COMPLETED' ||
                                data.type === 'MEMORY_INDIVIDUAL_STATUS_CHANGED'
                            ) {
                                refreshAllData();
                            }
                        } catch (e) {
                            // Ignorar mensajes no-JSON
                        }
                    };
                }
            } catch (err) {
                console.warn("Fallo no bloqueante al conectar WebSocket de administración:", err);
            }
        };

        connectWS();

        return () => {
            isMounted = false;
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.close();
                } catch (e) {
                    // Safe cleanup
                }
            }
        };
    }, [refreshAllData]);

    // ==========================================
    // MANEJADORES DE ACCIONES
    // ==========================================

    /**
     * Proyecta el código QR en la pantalla gigante vía WebSocket.
     */
    const handleSendQRToScreen = async () => {
        setIsActionLoading(true);
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: 'Proyectando QR de Memorama Individual en pantalla gigante...'
        });

        try {
            const res = await SendQROverWebSocket();
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Portada con QR proyectada en pantalla gigante con éxito!'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al proyectar QR en pantalla gigante.'
                });
            }
        } catch (error) {
            console.error("Error al enviar QR a pantalla:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al comunicar con la pantalla gigante.'
            });
        } finally {
            setIsActionLoading(false);
        }
    };

    /**
     * Alterna el estado activo / pausado de la dinámica.
     */
    const handleToggleStatus = async (newStatus) => {
        setIsActionLoading(true);
        try {
            const res = await ToggleAdminDynamicStatus(newStatus);
            if (res && res.success) {
                setIsDynamicActive(newStatus);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Dinámica ${newStatus ? 'activada' : 'pausada'} correctamente para aficionados.`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al cambiar estado de la dinámica.'
                });
            }
        } catch (error) {
            console.error("Error al alternar estado:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al actualizar estado de la dinámica.'
            });
        } finally {
            setIsActionLoading(false);
        }
    };

    /**
     * Alterna la designación de un aficionado como ganador oficial.
     */
    const handleToggleWinner = async (participant) => {
        const nextIsWinner = !participant.isWinner;
        try {
            const res = await SetParticipantWinner(participant.id, nextIsWinner, nextIsWinner ? 1 : 0);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextIsWinner
                        ? `¡${participant.name} fue marcado como ganador oficial!`
                        : `Se removió el estatus de ganador para ${participant.name}.`
                });
                refreshAllData();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al actualizar estatus de ganador.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al actualizar ganador.'
            });
        }
    };

    /**
     * Ejecuta la descalificación tras capturar el motivo en el modal.
     */
    const handleConfirmDisqualify = async (reason) => {
        if (!disqualifyTarget) return;
        setIsActionLoading(true);

        try {
            const res = await DisqualifyParticipant(disqualifyTarget.id, reason, 'ADMIN');
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `El aficionado ${disqualifyTarget.name} ha sido descalificado.`
                });
                setDisqualifyTarget(null);
                refreshAllData();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al descalificar al aficionado.'
                });
            }
        } catch (error) {
            console.error("Error al descalificar:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al procesar la descalificación.'
            });
        } finally {
            setIsActionLoading(false);
        }
    };

    /**
     * Revierte la descalificación de un aficionado.
     */
    const handleUndisqualify = async (participant) => {
        try {
            const res = await UndisqualifyParticipant(participant.id);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `El aficionado ${participant.name} ha sido restituido.`
                });
                refreshAllData();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al restituir al aficionado.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al restituir aficionado.'
            });
        }
    };

    /**
     * Descarga directa del archivo Excel oficial.
     */
    const handleExportExcel = () => {
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: 'Generando reporte ejecutivo oficial en Excel (.xlsx)...'
        });
        try {
            const url = GetExportExcelUrl();
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Memorama_Individual_Reporte_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            console.error("Error al descargar Excel:", e);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al generar la descarga del archivo Excel.'
            });
        }
    };

    /**
     * Confirma y ejecuta el reinicio de jornada en la zona de peligro.
     */
    const handleResetConfirm = async () => {
        try {
            const res = await ResetAdminDynamic();
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Dinámica reiniciada con éxito! Se limpiaron los registros y métricas de prueba.'
                });
                refreshAllData();
                return { success: true };
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al reiniciar la dinámica.'
                });
                return { success: false, message: res?.message };
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al reiniciar la dinámica.'
            });
            return { success: false, message: 'Error de conexión.' };
        }
    };

    const handleReturn = () => {
        if (getOutOfTheDynamic) {
            getOutOfTheDynamic();
        }
    };

    return (
        <SecondaryBase actionToExecute={handleReturn}>
            {/* Modal de Confirmación para Reinicio de Jornada */}
            {showResetConfirm && (
                <Confirm
                    message="⚠️ ATENCIÓN: ¿Estás seguro de que deseas reiniciar la dinámica de Memorama Individual? Esta acción limpiará todos los registros de aficionados, tiempos y métricas de prueba para dejar el padrón en ceros antes del partido oficial. Los temas visuales y la configuración de la dinámica se preservarán intactos."
                    actionToYesButton={handleResetConfirm}
                    textYesButton="Sí, reiniciar registros de prueba"
                    textNoButton="Cancelar"
                    setShowModal={setShowResetConfirm}
                />
            )}

            {/* Modal de Descalificación con Captura de Motivo */}
            <MemoryIndividualDisqualifyModal
                isOpen={Boolean(disqualifyTarget)}
                participant={disqualifyTarget}
                onClose={() => setDisqualifyTarget(null)}
                onConfirm={handleConfirmDisqualify}
                isLoading={isActionLoading}
            />

            {/* Modal de Gestión de Temas y Cartas (Selección 1 Clic) */}
            <MemoryIndividualThemeModal
                isOpen={isThemeModalOpen}
                onClose={() => setIsThemeModalOpen(false)}
                onThemeActivated={() => {
                    refreshAllData();
                }}
            />

            {/* Panel Principal */}
            <div className="container-fluid px-3 px-md-4 py-3 MemoryIndividualBoard">
                {/* ========================================== */}
                {/* CABECERA PRINCIPAL */}
                {/* ========================================== */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 pb-3 border-bottom">
                    <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h1 className="h3 fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                <i className="bi bi-grid-3x3-gap-fill text-primary"></i>
                                <span>Memorama Individual</span>
                            </h1>
                            <span className="badge bg-primary-subtle text-primary fw-bold font-monospace px-2.5 py-1">
                                Tile 51 - psl-memory
                            </span>
                            <span className={`badge ${isDynamicActive ? 'bg-success' : 'bg-secondary'} text-white fw-bold px-2.5 py-1`}>
                                {isDynamicActive ? '● En Vivo' : '○ Pausada'}
                            </span>
                        </div>
                        <p className="text-secondary small mb-0 mt-1 d-flex align-items-center gap-2 flex-wrap">
                            <span>Control central de estadio, embudo en tiempo real, padrón oficial y exportación.</span>
                            {lastSyncTime && (
                                <span className="text-muted">
                                    • Última sincronización: <strong>{lastSyncTime}</strong>
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-outline-secondary fw-semibold d-flex align-items-center gap-2 shadow-sm rounded-pill px-3"
                            onClick={refreshAllData}
                            disabled={isLoading}
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin' : ''}`}></i>
                            <span>Refrescar</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-dark fw-semibold d-flex align-items-center gap-2 rounded-pill px-3"
                            onClick={handleReturn}
                        >
                            <i className="bi bi-box-arrow-left"></i>
                            <span>Dashboard</span>
                        </button>
                    </div>
                </div>

                {/* ========================================== */}
                {/* BARRA DE HERRAMIENTAS Y CONTROL DE ESTADIO */}
                {/* ========================================== */}
                <div className="card border-0 shadow-sm rounded-4 p-3 mb-4 bg-white">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {/* Botón de Alto Impacto: Proyección QR */}
                            <button
                                type="button"
                                className="btn btn-danger fw-bold d-flex align-items-center gap-2 shadow-sm px-3 py-2 rounded-pill"
                                onClick={handleSendQRToScreen}
                                disabled={isActionLoading}
                            >
                                <i className="bi bi-broadcast fs-5"></i>
                                <span>Mostrar QR en Pantalla Gigante</span>
                            </button>

                            {/* Botón de Gestión de Temas */}
                            <button
                                type="button"
                                className="btn btn-primary fw-bold d-flex align-items-center gap-2 shadow-sm px-3 py-2 rounded-pill"
                                onClick={() => setIsThemeModalOpen(true)}
                            >
                                <i className="bi bi-palette2 fs-5"></i>
                                <span>Gestión de Temas y Cartas</span>
                            </button>

                            {/* Botón de Exportación a Excel */}
                            <button
                                type="button"
                                className="btn btn-success fw-bold d-flex align-items-center gap-2 shadow-sm px-3 py-2 rounded-pill"
                                onClick={handleExportExcel}
                            >
                                <i className="bi bi-file-earmark-excel-fill fs-5"></i>
                                <span>Exportar Padrón a Excel (.xlsx)</span>
                            </button>
                        </div>

                        {/* Toggle de Dinámica Activa / Pausada */}
                        <div className="form-check form-switch d-flex align-items-center gap-2 mb-0 p-0">
                            <label
                                className={`form-check-label fw-bold small ${isDynamicActive ? 'text-success' : 'text-secondary'}`}
                                htmlFor="toggleDynamicStatusSwitch"
                            >
                                {isDynamicActive ? 'Dinámica ACTIVA' : 'Dinámica PAUSADA'}
                            </label>
                            <input
                                className="form-check-input ms-0"
                                type="checkbox"
                                role="switch"
                                id="toggleDynamicStatusSwitch"
                                style={{ width: '2.8em', height: '1.4em', cursor: 'pointer' }}
                                checked={isDynamicActive}
                                onChange={(e) => handleToggleStatus(e.target.checked)}
                                disabled={isActionLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* ========================================== */}
                {/* MONITOR DE EMBUDO DE CONVERSIÓN (LIVE FUNNEL) */}
                {/* ========================================== */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-funnel-fill text-primary"></i>
                            <span>Embudo de Conversión en Tiempo Real</span>
                        </h5>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="badge bg-light text-dark border px-2.5 py-1.5 fw-semibold rounded-pill">
                                Registrados: <strong>{stats.totalParticipants}</strong>
                            </span>
                            <span className="badge bg-light text-primary border px-2.5 py-1.5 fw-semibold rounded-pill">
                                Completados: <strong>{stats.completedParticipants}</strong>
                            </span>
                            <span className="badge bg-light text-success border px-2.5 py-1.5 fw-semibold rounded-pill">
                                Ganadores: <strong>{stats.winnerParticipants}</strong>
                            </span>
                            <span className="badge bg-light text-danger border px-2.5 py-1.5 fw-semibold rounded-pill">
                                Descalificados: <strong>{stats.disqualifiedParticipants}</strong>
                            </span>
                        </div>
                    </div>

                    <div className="row g-3">
                        {/* 1. Escaneos QR */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <div className="card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center MemoryIndividualBoard__MetricCard MemoryIndividualBoard__MetricCard--primary">
                                <div className="MemoryIndividualBoard__MetricIconBox MemoryIndividualBoard__MetricIconBox--primary mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-qr-code-scan fs-5"></i>
                                </div>
                                <span className="small text-secondary fw-semibold d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                                    1. Escaneos QR
                                </span>
                                <h3 className="fw-bold text-dark font-monospace mb-0 mt-1">
                                    {stats.qrScan.toLocaleString('es-MX')}
                                </h3>
                            </div>
                        </div>

                        {/* 2. Visitas Web */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <div className="card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center MemoryIndividualBoard__MetricCard MemoryIndividualBoard__MetricCard--info">
                                <div className="MemoryIndividualBoard__MetricIconBox MemoryIndividualBoard__MetricIconBox--info mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-eye-fill fs-5"></i>
                                </div>
                                <span className="small text-secondary fw-semibold d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                                    2. Visitas Web
                                </span>
                                <h3 className="fw-bold text-dark font-monospace mb-0 mt-1">
                                    {stats.pageView.toLocaleString('es-MX')}
                                </h3>
                            </div>
                        </div>

                        {/* 3. Inicios Formulario */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <div className="card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center MemoryIndividualBoard__MetricCard MemoryIndividualBoard__MetricCard--warning">
                                <div className="MemoryIndividualBoard__MetricIconBox MemoryIndividualBoard__MetricIconBox--warning mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-ui-checks fs-5"></i>
                                </div>
                                <span className="small text-secondary fw-semibold d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                                    3. Inicios Form
                                </span>
                                <h3 className="fw-bold text-dark font-monospace mb-0 mt-1">
                                    {stats.formStart.toLocaleString('es-MX')}
                                </h3>
                            </div>
                        </div>

                        {/* 4. Registros Completados */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <div className="card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center MemoryIndividualBoard__MetricCard MemoryIndividualBoard__MetricCard--success">
                                <div className="MemoryIndividualBoard__MetricIconBox MemoryIndividualBoard__MetricIconBox--success mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-person-check-fill fs-5"></i>
                                </div>
                                <span className="small text-secondary fw-semibold d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                                    4. Registrados
                                </span>
                                <h3 className="fw-bold text-dark font-monospace mb-0 mt-1">
                                    {stats.formComplete.toLocaleString('es-MX')}
                                </h3>
                            </div>
                        </div>

                        {/* 5. Juegos Iniciados */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <div className="card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center MemoryIndividualBoard__MetricCard MemoryIndividualBoard__MetricCard--indigo">
                                <div className="MemoryIndividualBoard__MetricIconBox MemoryIndividualBoard__MetricIconBox--indigo mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-controller fs-5"></i>
                                </div>
                                <span className="small text-secondary fw-semibold d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                                    5. Partidas Ini
                                </span>
                                <h3 className="fw-bold text-dark font-monospace mb-0 mt-1">
                                    {stats.gameStart.toLocaleString('es-MX')}
                                </h3>
                            </div>
                        </div>

                        {/* 6. Juegos Completados */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <div className="card h-100 border-0 shadow-sm rounded-4 p-3 bg-white text-center MemoryIndividualBoard__MetricCard MemoryIndividualBoard__MetricCard--danger">
                                <div className="MemoryIndividualBoard__MetricIconBox MemoryIndividualBoard__MetricIconBox--danger mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-trophy-fill fs-5"></i>
                                </div>
                                <span className="small text-secondary fw-semibold d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                                    6. Completados
                                </span>
                                <h3 className="fw-bold text-dark font-monospace mb-0 mt-1">
                                    {stats.gameComplete.toLocaleString('es-MX')}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ========================================== */}
                {/* PADRÓN OFICIAL DE AFICIONADOS */}
                {/* ========================================== */}
                <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden mb-4">
                    {/* Filtros y Búsqueda */}
                    <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: '420px' }}>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 ps-0"
                                    placeholder="Buscar por nombre, correo o celular..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Pestañas de Estatus */}
                        <div className="btn-group btn-group-sm" role="group">
                            <button
                                type="button"
                                className={`btn ${statusFilter === 'all' ? 'btn-primary fw-bold' : 'btn-outline-secondary'}`}
                                onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
                            >
                                Todos ({stats.totalParticipants})
                            </button>
                            <button
                                type="button"
                                className={`btn ${statusFilter === 'completed' ? 'btn-primary fw-bold' : 'btn-outline-secondary'}`}
                                onClick={() => { setStatusFilter('completed'); setCurrentPage(1); }}
                            >
                                Completados ({stats.completedParticipants})
                            </button>
                            <button
                                type="button"
                                className={`btn ${statusFilter === 'winner' ? 'btn-primary fw-bold' : 'btn-outline-secondary'}`}
                                onClick={() => { setStatusFilter('winner'); setCurrentPage(1); }}
                            >
                                Ganadores ({stats.winnerParticipants})
                            </button>
                            <button
                                type="button"
                                className={`btn ${statusFilter === 'disqualified' ? 'btn-primary fw-bold' : 'btn-outline-secondary'}`}
                                onClick={() => { setStatusFilter('disqualified'); setCurrentPage(1); }}
                            >
                                Descalificados ({stats.disqualifiedParticipants})
                            </button>
                        </div>

                        {/* Registros por página */}
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="pageSizeSelect" className="small text-muted mb-0">Filas:</label>
                            <select
                                id="pageSizeSelect"
                                className="form-select form-select-sm"
                                style={{ width: '75px' }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    {/* Tabla de Participantes */}
                    <div className="table-responsive">
                        <table className="table table-hover table-striped align-middle mb-0 MemoryIndividualBoard__Table">
                            <thead className="table-light text-secondary small text-uppercase">
                                <tr>
                                    <th style={{ width: '60px' }} className="text-center">#</th>
                                    <th>Aficionado</th>
                                    <th>Contacto</th>
                                    <th className="text-center" style={{ width: '90px' }}>Abonado</th>
                                    <th className="text-center">Tiempo Oficial</th>
                                    <th className="text-center">Intentos</th>
                                    <th className="text-center">Estado</th>
                                    <th>Fecha Registro</th>
                                    <th style={{ width: '130px' }} className="text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {participants.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5 text-muted">
                                            <i className="bi bi-inbox fs-3 d-block mb-2 text-secondary"></i>
                                            {isLoading ? 'Cargando padrón oficial...' : 'No se encontraron aficionados en este filtro.'}
                                        </td>
                                    </tr>
                                ) : (
                                    participants.map((p) => {
                                        const isWinner = Boolean(p.isWinner);
                                        const isDisqualified = Boolean(p.isDisqualified);
                                        const hasCompleted = Boolean(p.hasCompleted);

                                        return (
                                            <tr key={p.id} className={isDisqualified ? 'table-danger-subtle' : ''}>
                                                {/* Rank oficial */}
                                                <td className="text-center font-monospace fw-bold">
                                                    {p.rank && p.rank > 0 ? (
                                                        <span className={`badge ${p.rank <= 3 ? 'bg-warning text-dark' : 'bg-light text-dark border'} rounded-pill px-2`}>
                                                            {p.rank <= 3 ? `🏆 ${p.rank}` : p.rank}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>

                                                {/* Nombre completo */}
                                                <td>
                                                    <div className="fw-bold text-dark">{p.name}</div>
                                                    <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                        ID #{p.id} {p.source ? `• Origen: ${p.source}` : ''}
                                                    </small>
                                                </td>

                                                {/* Contacto: Email y Teléfono */}
                                                <td>
                                                    <div className="small text-dark font-monospace text-truncate" style={{ maxWidth: '200px' }} title={p.email}>
                                                        <i className="bi bi-envelope text-secondary me-1"></i>
                                                        {p.email}
                                                    </div>
                                                    <div className="small text-muted font-monospace">
                                                        <i className="bi bi-telephone text-secondary me-1"></i>
                                                        {p.phone}
                                                    </div>
                                                </td>

                                                {/* Condición de Abonado */}
                                                <td className="text-center">
                                                    {p.isAbonado || p.abonadoOption === 'Sí' ? (
                                                        <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 rounded-pill fw-semibold" style={{ fontSize: '0.75rem' }}>
                                                            <i className="bi bi-star-fill me-1"></i>Sí
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-secondary-subtle text-secondary px-2 py-1 rounded-pill" style={{ fontSize: '0.75rem' }}>
                                                            No
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Tiempo Oficial */}
                                                <td className="text-center font-monospace fw-bold fs-6">
                                                    {hasCompleted ? (
                                                        <span className="text-primary">{p.formattedTime || '00:00.000'}</span>
                                                    ) : (
                                                        <span className="text-muted small">Pendiente</span>
                                                    )}
                                                </td>

                                                {/* Intentos */}
                                                <td className="text-center font-monospace">
                                                    {hasCompleted ? (
                                                        <span className="badge bg-secondary-subtle text-secondary rounded-pill px-2">
                                                            {p.totalAttempts || 0}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>

                                                {/* Estado */}
                                                <td className="text-center">
                                                    {isDisqualified ? (
                                                        <span
                                                            className="badge bg-danger text-white rounded-pill px-2.5 py-1"
                                                            title={`Motivo: ${p.disqualifiedReason || 'Descalificado por auditoría'}`}
                                                        >
                                                            <i className="bi bi-slash-circle me-1"></i>
                                                            Descalificado
                                                        </span>
                                                    ) : isWinner ? (
                                                        <span className="badge bg-success text-white rounded-pill px-2.5 py-1">
                                                            <i className="bi bi-trophy-fill me-1 text-warning"></i>
                                                            Ganador
                                                        </span>
                                                    ) : hasCompleted ? (
                                                        <span className="badge bg-primary text-white rounded-pill px-2.5 py-1">
                                                            <i className="bi bi-check-circle me-1"></i>
                                                            Completado
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-secondary-subtle text-secondary rounded-pill px-2 py-1">
                                                            Pendiente
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Fecha de Registro */}
                                                <td className="small text-secondary">
                                                    {formatMexicoDate(p.registeredAtUtc)}
                                                </td>

                                                {/* Acciones */}
                                                <td className="text-center">
                                                    <div className="btn-group btn-group-sm" role="group">
                                                        {/* Botón Ganador */}
                                                        {!isDisqualified && (
                                                            <button
                                                                type="button"
                                                                className={`btn ${isWinner ? 'btn-warning text-dark' : 'btn-outline-secondary'}`}
                                                                title={isWinner ? 'Quitar estatus de ganador' : 'Marcar como ganador oficial'}
                                                                onClick={() => handleToggleWinner(p)}
                                                            >
                                                                <i className={`bi ${isWinner ? 'bi-trophy-fill' : 'bi-trophy'}`}></i>
                                                            </button>
                                                        )}

                                                        {/* Botón Descalificar o Restituir */}
                                                        {isDisqualified ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-success"
                                                                title="Restituir aficionado en la dinámica"
                                                                onClick={() => handleUndisqualify(p)}
                                                            >
                                                                <i className="bi bi-arrow-counterclockwise"></i>
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger"
                                                                title="Descalificar aficionado por auditoría"
                                                                onClick={() => setDisqualifyTarget(p)}
                                                            >
                                                                <i className="bi bi-shield-x"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    <div className="p-3 border-top bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <small className="text-muted">
                            Mostrando página <strong>{currentPage}</strong> de <strong>{totalPages}</strong> ({totalParticipantsCount} aficionados)
                        </small>

                        <div className="btn-group btn-group-sm">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                disabled={currentPage <= 1 || isLoading}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            >
                                <i className="bi bi-chevron-left me-1"></i> Anterior
                            </button>
                            <span className="btn btn-light disabled text-dark fw-bold px-3">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                disabled={currentPage >= totalPages || isLoading}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            >
                                Siguiente <i className="bi bi-chevron-right ms-1"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ========================================== */}
                {/* ZONA DE PELIGRO (DANGER ZONE) */}
                {/* ========================================== */}
                <div className="card border-danger rounded-4 p-4 bg-white shadow-sm mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h5 className="fw-bold text-danger mb-1 d-flex align-items-center gap-2">
                                <i className="bi bi-exclamation-triangle-fill"></i>
                                <span>Zona de Peligro — Limpieza de Registros de Jornada</span>
                            </h5>
                            <p className="text-secondary small mb-0" style={{ maxWidth: '780px' }}>
                                Esta operación purga de manera permanente todas las participaciones, marcas de tiempo y métricas de telemetría de prueba antes del silbatazo inicial del partido. <strong>Los temas visuales y la configuración de la dinámica se mantienen íntegros.</strong>
                            </p>
                        </div>

                        <button
                            type="button"
                            className="btn btn-outline-danger fw-bold px-4 py-2 rounded-pill shadow-sm d-flex align-items-center gap-2"
                            onClick={() => setShowResetConfirm(true)}
                        >
                            <i className="bi bi-trash3-fill"></i>
                            <span>Reiniciar Dinámica (Reset de Jornada)</span>
                        </button>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
}

export default MemoryIndividualBoard;
